import { ClinicalArchive } from '.';
import { LoadableDocConfig } from '../generalSettings';

export const sleepApneaDocumentTypes: LoadableDocConfig[] = [
  {
    name: 'diagnosis',
    subTypes: ['diagnosis', 'sleepApneaFirstVisit', 'respiratoryFirstVisit', 'sleepApneaSeverity', 'performanceRating'],
  },
  {
    name: 'clpAndCnpTests',
    subTypes: ['sleepPolygraphy', 'sleepStudy', 'spirometry', 'nightOximetry', 'carbonDioxideMeasurement'],
    secondaryDocs: ['madTherapy', 'diagnosis'],
  },
  { name: 'examination' },
  { name: 'medication', subTypes: ['medication', 'adverseEffect'] },
  {
    name: 'treatment',
    subTypes: [
      'papTherapy',
      'hfncTherapy',
      'oxygenTherapy',
      'madTherapy',
      'surgicalTreatment',
      'otherTreatment',
      'patientDoesNotWantRespiratorySupportTherapy',
    ],
    secondaryDocs: ['diagnosis', 'sleepApneaFirstVisit', 'respiratoryFirstVisit', 'sleepPolygraphy', 'sleepStudy'],
  },
  { name: 'checklist' },
  {
    name: 'comorbidity',
    subTypes: ['comorbidity', 'sickLeave'],
    secondaryDocs: ['diagnosis'],
    title: 'otherHealth',
  },
  { name: 'background', subTypes: ['background', 'measurement'] },
  { name: 'myService', title: 'titleShort' },
];

export const sleepApneaGraphDocTypes = [
  'diagnosis',
  'medication',
  'adverseEffect',
  'papTherapy',
  'hfncTherapy',
  'oxygenTherapy',
  'madTherapy',
  'surgicalTreatment',
  'otherTreatment',
  'patientDoesNotWantRespiratorySupportTherapy',
  'sleepPolygraphy',
  'sleepStudy',
  'spirometry',
  'nightOximetry',
  'carbonDioxideMeasurement',
  'comorbidity',
  'background',
  'measurement',
];

const respiratoryFailureDGs = ['J96.1', 'J96.9', 'respiratoryFailureSuspicion'];

// Filter these routes/types from showing in page navigations based on these rules
export const sleepApneaFilteredTypes = (docs?: INeuroUIDocument[]): string[] => {
  let diag = undefined;

  if (docs && docs.length > 0) {
    diag = docs.find(d => respiratoryFailureDGs.includes((d as IDiagnosis).diagnosis ?? ''));
  }
  return [
    ...(diag ? [] : ['checklist']), // Filter checklist if no Respiratory dx
  ];
};

export const sleepApneaClinicalArchiveTypes: readonly ClinicalArchive[] = ['KEU', 'HOI'] as const;

export const sleepApneaAlerts: IAlert[] = [
  {
    // Notification to the user if respiratory failure treatments are recorded but no respiratory failure diagnosis is found
    content: 'alerts.sleepApnea.noRespiratoryFailureDiagnosis',
    id: 'noRespiratoryFailureDiagnosis',
    severity: 'warning',
    popup: true,
    rule: (docs?: Array<IControlProps & Record<string, unknown>>): boolean => {
      const respiratoryDG = docs?.find(
        d => d._type === 'diagnosis' && respiratoryFailureDGs.includes((d as IDiagnosis).diagnosis ?? ''),
      );
      if (respiratoryDG) return false;

      const hasOxygenOrHFNCDoc = docs?.find(d => d._type === 'oxygenTherapy' || d._type === 'hfncTherapy');

      const deviceTypeOptions: Array<IPapDevice['type']> = [
        'constantPressure2PV',
        'selfAdjusting2PV',
        'adaptiveServoVentilator',
        'automaticAdaptiveServoVentilator',
        'VAPS',
      ];
      const hasPapTherapyWithRelevantDevice = docs?.find(d => {
        if (d._type === 'papTherapy') {
          // If papTherapy includes a device that is not a CPAP device
          return (d as IPAPTherapy).devices?.find(device => deviceTypeOptions.includes(device.type));
        }
        return false;
      });
      return !!hasOxygenOrHFNCDoc || !!hasPapTherapyWithRelevantDevice;
    },
  },
];
